import { logger } from '@/ui'
import { WidgetInputToggleField } from '@/ui/rapid-ui/inputs/widgets/widget-input-toggle-field'
import ctl from '@netlify/classnames-template-literals'
import { ClipboardCopyIcon, ExternalLinkIcon, RssIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { CareerSiteViewModel } from './career-site.vm'

export type CareerSiteSettingsProps = CareerSiteViewModel

function CopyButton({ text }: { text: string }) {
	const { t } = useTranslation('common')
	const [copied, setCopied] = useState(false)

	const handleCopy = () => {
		void navigator.clipboard.writeText(text).then(() => {
			setCopied(true)
			setTimeout(() => setCopied(false), 2000)
		}).catch((error) => {
			logger.error(error, { tags: { component: 'CopyButton', action: 'clipboard-copy' } })
		})
	}

	return (
		<button
			type="button"
			onClick={handleCopy}
			className={ctl(`
				flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg
				border border-grey-200 hover:bg-grey-50 transition-colors
				${copied ? 'text-green-600 border-green-200 bg-green-50' : 'text-grey-700'}
			`)}
		>
			<ClipboardCopyIcon className="size-4" />
			{copied ? t('careerSite.copied') : t('careerSite.copy')}
		</button>
	)
}

export function CareerSiteSettings({ view, loading, events }: CareerSiteSettingsProps) {
	const { t } = useTranslation('common')

	return (
		<div className="space-y-8 max-w-[600px]">
			<div className="space-y-2">
				<p className="text-grey-600">
					{t('careerSite.description')}
				</p>
			</div>

			<div className="flex items-center justify-between p-4 bg-grey-50 rounded-lg border border-grey-100">
				<div className="space-y-1">
					<div className="font-medium text-grey-900">{t('careerSite.title')}</div>
					<div className="text-sm text-grey-600">
						{view.careerSiteEnabled
							? t('careerSite.statusEnabled')
							: t('careerSite.statusDisabled')}
					</div>
				</div>
				{view.canToggle && (
					<WidgetInputToggleField
						value={view.careerSiteEnabled}
						onChange={events.onToggleCareerSite}
						disabled={loading.saving}
						ariaLabel={t('careerSite.toggleAriaLabel')}
					/>
				)}
			</div>

			{view.careerSiteEnabled && (
				<>
					<div className="space-y-3">
						<div className="text-sm font-medium text-grey-700">{t('careerSite.publicUrl')}</div>
						<div className="flex items-center gap-2">
							<div
								className={ctl(`
									flex-1 px-3 py-2 bg-white border border-grey-200 rounded-lg
									text-sm text-grey-800 font-mono truncate
								`)}
							>
								{view.publicUrl}
							</div>
							<CopyButton text={view.publicUrl} />
							<a
								href={view.publicUrl}
								target="_blank"
								rel="noopener noreferrer"
								className={ctl(`
									flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg
									border border-grey-200 hover:bg-grey-50 transition-colors text-grey-700
								`)}
							>
								<ExternalLinkIcon className="size-4" />
								{t('careerSite.open')}
							</a>
						</div>
						<p className="text-xs text-grey-500">
							{t('careerSite.publicUrlHint')}
						</p>
					</div>

					{view.rssFeedUrl && (
						<div className="space-y-3">
							<div className="flex items-center gap-1.5 text-sm font-medium text-grey-700">
								<RssIcon className="size-4" />
								{t('careerSite.rssFeed')}
							</div>
							<div className="flex items-center gap-2">
								<div
									className={ctl(`
										flex-1 px-3 py-2 bg-white border border-grey-200 rounded-lg
										text-sm text-grey-800 font-mono truncate
									`)}
								>
									{view.rssFeedUrl}
								</div>
								<CopyButton text={view.rssFeedUrl} />
							</div>
							<p className="text-xs text-grey-500">
								{t('careerSite.rssFeedHint')}
							</p>
						</div>
					)}
				</>
			)}
		</div>
	)
}
